import { compare, Operator } from "./utils";

export interface ValidatedForm {
  formName: string;
  templateKey: string;
  templateName: string;
  fields: Record<string, any>;
}
interface Rule {
  description: string;
  ruleVersion: string;
  ruleDisabled?: boolean;
  value1: string[];
  value2: any[];
  operator: Operator;
  reducer?: "&&" | "||";
  allowEmptyValue1?: boolean;
}
type Op = string | { formName: string; templateKey: string; templateName: string; fieldKeyRegex: string };
export interface ValidationError {
  rule: any;
  message: string;
  key: string;
}

function reduceResult(r1: boolean, r2: boolean, reducer: "&&" | "||") {
  if (reducer === "&&") {
    return r1 && r2;
  } else if (reducer === "||") {
    return r1 || r2;
  } else {
    throw new Error("Invalid code path");
  }
}
function evalExpr(params: {
  operand1: { value: string }[] | { value: string };
  operand2: "" | { value: string };
  operator: Operator;
  reducer?: "&&" | "||";
}) {
  let value2: number | string;
  if (params.operand2 === "") {
    value2 = "";
  } else {
    const parsed = parseFloat(params.operand2.value);
    value2 = isNaN(parsed) ? params.operand2.value : parsed;
  }

  if (Array.isArray(params.operand1)) {
    return params.operand1.reduce((prev, op1) => {
      const parsed = parseFloat(op1.value);
      const value = isNaN(parsed) ? op1.value : parsed;
      const tempResult = compare(value, params.operator, value2);
      return reduceResult(prev, tempResult, params.reducer!);
    }, true);
  } else {
    const parsed = parseFloat(params.operand1.value);
    const value = isNaN(parsed) ? params.operand1.value : parsed;
    return compare(value, params.operator, value2);
  }
}

function getFormFieldsFromOperand(fields: Record<string, any>, op: { fieldKeyRegex: string }) {
  const r = new RegExp(op.fieldKeyRegex);
  return Object.entries(fields)
    .filter(([key, _]) => r.test(key))
    .map(([_, value]) => value);
}

/**
 * When checking value2, it can be a form or a single value
 */
function getFormValues(forms: ValidatedForm[], op: Op) {
  if (typeof op === "string") {
    return [op];
  }

  let filteredForms = forms;
  if (op.formName) {
    filteredForms = forms.filter((f) => f.formName.toLowerCase() === op.formName.toLowerCase());
  } else if (op.templateKey) {
    filteredForms = forms.filter((f) => f.templateKey.toLowerCase() === op.templateKey.toLowerCase());
  } else if (op.templateName) {
    filteredForms = forms.filter((f) => f.templateName.toLowerCase() === op.templateName.toLowerCase());
  }

  return filteredForms.flatMap((form) => getFormFieldsFromOperand(form.fields, op));
}
function getManyForms(forms: ValidatedForm[], values: Op[] | Op[][]) {
  return values.map((v1) => {
    if (Array.isArray(v1)) {
      return v1.flatMap((v) => getFormValues(forms, v));
    } else {
      return getFormValues(forms, v1);
    }
  });
}
function transpose<T>(values: T[][]) {
  return values[0].map((col, i) => values.map((row) => row[i]));
}
function validateFormsWithRule(params: { forms: ValidatedForm[]; rule: Rule }) {
  const errorKeys: ValidationError[] = [];
  const { forms, rule } = params;
  let value1 = getManyForms(forms, rule.value1);
  const value2 = getManyForms(forms, [rule.value2])[0][0];
  if (typeof value2 === "undefined") {
    return errorKeys;
  }

  if (rule.reducer && Array.isArray(value1[0])) {
    value1 = transpose(value1);
    value1 = [value1];
  }

  let result;
  value1.forEach((v1) => {
    if (v1.length === 0) {
      return;
    }
    if (rule.reducer) {
      v1.forEach((v) => {
        if (
          typeof rule.allowEmptyValue1 !== "undefined" &&
          !rule.allowEmptyValue1 &&
          (typeof v[0].value === "undefined" || v[0].value === "")
        ) {
          result = true;
        } else {
          result = evalExpr({
            operand1: v,
            operator: rule.operator,
            operand2: value2,
            reducer: rule.reducer,
          });
        }
        if (!result) {
          errorKeys.push({
            rule,
            message: rule.description,
            key: v[0].key,
          });
        }
      });
    } else {
      result = evalExpr({
        operand1: v1[0],
        operator: rule.operator,
        operand2: value2,
      });
      if (!result) {
        errorKeys.push({
          rule,
          message: rule.description,
          key: v1[0].key,
        });
      }
    }
  });
  return errorKeys;
}

/**
 * Checks required fields in rule
 * Accepts only certain rule versions
 *
 * @example rule version 1.0
 *  ruleVersion String
 *  description String
 *  value1 Array
 *  operator String
 *  value2 Array
 *  ruleDisabled Boolean (optional)
 */
function isValidRule(rule: Rule) {
  const requiredRuleKeys = ["ruleVersion", "description", "value1", "operator", "value2"],
    allowedRuleVersions = ["1.0"];
  if (requiredRuleKeys.some((k) => !rule.hasOwnProperty(k))) {
    console.error("Rule missing required fields", {
      rule,
      requiredRuleKeys,
    });
    return false;
  }
  if (allowedRuleVersions.indexOf(rule.ruleVersion) === -1) {
    console.error("Rule version not acceptable", {
      rule,
      allowedRuleVersions,
    });
    return false;
  }
  return !rule.ruleDisabled;
}

const validationUtils = {
  validateAll(params: { forms: ValidatedForm[]; rules: Rule[] }) {
    const { forms, rules } = params;

    return rules.filter(isValidRule).flatMap((rule) => validateFormsWithRule({ forms, rule }));
  },
};
export default validationUtils;
